import { CLIENT_CONFLICT_MARKER } from '../../lib/clientConflict';
/**
 * Transient-vs-terminal failure split for the reversible ledger actions (#266).
 *
 * Those actions are optimistic: a click flips the row instantly and a rejection
 * rolls it back. This module makes the (rare) rejection legible by splitting it
 * into two very different experiences:
 *
 *  - TERMINAL — a stale-version conflict or a validation refusal. The server
 *    answered "no", so a blind retry just re-conflicts. It surfaces at once (the
 *    inline error + a Retry that first re-reads the reconciled version).
 *  - TRANSIENT — a raw `fetch` rejection (a network blip, no HTTP response). A
 *    quiet retry usually succeeds, so we auto-retry (bounded, with backoff) and
 *    the treasurer never sees a blip that would have resolved itself.
 *
 * The split is structural, needing no per-hook error-class imports: every
 * terminal failure in these hooks is thrown AFTER an HTTP response and carries a
 * numeric `status` (ContributionConfirmError, ExpenditureConfirmError,
 * StagedRowUpdateError, BankTransaction*Error, ContributionRefundLinkError) — or
 * is the bare `CLIENT_CONFLICT_MARKER` a couple of endpoints throw. A
 * network failure rejects the fetch BEFORE any response, so it carries no status;
 * that absence is the transient marker.
 */

/** How many silent retries a transient failure gets before it surfaces. */
export const TRANSIENT_RETRY_LIMIT = 2;

/**
 * True when `error` is a transient network failure (a raw fetch rejection with no
 * HTTP response) rather than a terminal server answer. Terminal errors carry a
 * numeric `status`; the bare conflict marker is terminal too.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  ) {
    return false;
  }
  if (error.message === CLIENT_CONFLICT_MARKER) return false;
  return true;
}

/** Exponential backoff (ms) between silent retries, capped so it stays snappy. */
export function transientRetryDelay(attemptIndex: number): number {
  return Math.min(300 * 2 ** attemptIndex, 3000);
}

/**
 * Spread into a reversible ledger mutation's `useMutation` options. Only a
 * transient network failure is retried; a terminal error stops immediately so its
 * `onError` rollback + inline Retry fire at once. Because TanStack runs `onMutate`
 * once and defers `onError`/`onSuccess` until the final attempt, the optimistic
 * row never flickers between silent retries.
 */
export const transientRetry = {
  // TanStack invokes this after each failed attempt with a 0-based failureCount,
  // so `< LIMIT` yields exactly 1 initial attempt + LIMIT silent retries.
  retry: (failureCount: number, error: unknown) =>
    isTransientNetworkError(error) && failureCount < TRANSIENT_RETRY_LIMIT,
  retryDelay: transientRetryDelay,
};
